import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { of, ReplaySubject, Subject, zip } from 'rxjs';
import { concatMap, first, map, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';

import { Notification } from '@app/notifications/notification-api.model';
import {
  ResourceSuggestionWithDetails,
  SuggestedResourcesSectionWithDetails,
} from '@app/learner-home/learner-home-api.model';

import { FeedVideoModalService } from '@app/learner-home/components/feed-video-modal/feed-video-modal.service';
import { SuggestedResourcesService } from '@app/learner-home/services/suggested-resources.service';
import { NotificationService } from '@app/notifications/notification.service';
import { AuthService } from '@app/shared/services/auth.service';
import { NotifierService } from '@app/shared/services/notifier.service';
import { TranslateService } from '@ngx-translate/core';

import { VendorIntegrationsService } from '@app/shared/services/vendor-integrations.service';
import { LearnerHomeTrackerService } from '@app/learner-home/services/learner-home-tracker.service';
import { OrgAnnouncements } from '@app/orgs/services/org-announcements.service';
import { TagsService } from '@app/tags/services/tags.service';

interface Section {
  isExpanded: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  section$: Subject<SuggestedResourcesSectionWithDetails>;
}

type NotificationWithAnnouncements = Omit<Notification, 'parameters'> & {
  parameters: OrgAnnouncements;
};

@Component({
  selector: 'dgx-learner-home-discover',
  templateUrl: './learner-home-discover.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LearnerHomeDiscoverComponent
  extends SubscriberBaseDirective
  implements OnInit, OnDestroy
{
  @Input() public rowLength: number;

  public readonly CLASS_NAME = 'LearnerHomeDiscover';

  public orgAnnouncementNotification: NotificationWithAnnouncements;
  public sections: Section[] = [];
  public showSidebar = true;

  public skipLinkText = this.translateService.instant('dgSkipNav_SkipSidebar');
  public discoverHeader = this.translateService.instant('Core_Discover');

  private sectionsToLoad$ = this.createSectionLoader(this.sections);

  constructor(
    private cdr: ChangeDetectorRef,
    private element: ElementRef,
    private tagsService: TagsService,
    private notifierService: NotifierService,
    private suggestedResourcesService: SuggestedResourcesService,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private feedVideoModalService: FeedVideoModalService,
    private activatedRoute: ActivatedRoute,
    private vendorIntegrationService: VendorIntegrationsService,
    private learnerHomeTrackerService: LearnerHomeTrackerService
  ) {
    super();
  }

  public ngOnInit() {
    if (this.authService.authUser.enableOsanoCookieBannerAuthed) {
      this.vendorIntegrationService.loadOsanoCookieHandler(true);
    }

    // Load the first section, the rest will be handled by the lazy loading pipeline
    this.sectionsToLoad$.next(0);

    // show modal to complete self recorded video if url param
    const videoRecordingParams = this.activatedRoute.snapshot.queryParams;
    if (videoRecordingParams?.authored === 'Video') {
      this.feedVideoModalService
        .create({
          initialModel: {
            mediaLength: Number.parseInt(videoRecordingParams.seconds, 10),
            entryUrl: videoRecordingParams.url,
          },
        })
        .subscribe();
    }

    this.learnerHomeTrackerService.leanerHomeViewed(this.element.nativeElement);

    // check for org announcements
    if (this.authService.authUser?.defaultOrgId) {
      this.notificationService
        .getNotificationsOfType(0, 1, 'feed')
        .subscribe((orgAnnouncements) => {
          if (orgAnnouncements.items?.length) {
            const item = orgAnnouncements.items[0];
            this.orgAnnouncementNotification = item.readFlag
              ? null
              : (item as NotificationWithAnnouncements);
          }
        });
    }

    this.tagsService.userTagsModified
      .pipe(this.takeUntilDestroyed())
      .subscribe((payload) => {
        this.sections[0]?.section$
          .pipe(
            first(),
            switchMap((oldSection) =>
              this.suggestedResourcesService
                .getSuggestedResources({
                  section: oldSection.resourceSection,
                  useResourceImages: true,
                  take: oldSection.resourceSuggestions.length,
                })
                .pipe(
                  tap((newSection) => {
                    return this.updateSection(
                      oldSection,
                      newSection[0].resourceSuggestions,
                      payload.tagId
                    );
                  })
                )
            )
          )
          .subscribe((section) => {
            this.sections[0].section$.next(section[0]);
          });
      });
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.sectionsToLoad$.complete();
    this.cdr.detectChanges();
  }

  public updateSection(
    oldSection: SuggestedResourcesSectionWithDetails,
    newSection: ResourceSuggestionWithDetails[],
    tagId: number
  ) {
    if (!newSection[0]) {
      return;
    }

    const updatedResourceSuggestion = newSection.find(
      (suggestion) => suggestion.referenceId === tagId
    );
    const suggestionIndex = oldSection.resourceSuggestions.findIndex(
      (suggestion) =>
        suggestion.referenceId === updatedResourceSuggestion?.referenceId
    );

    if (!!updatedResourceSuggestion) {
      oldSection.resourceSuggestions.splice(
        suggestionIndex,
        1,
        updatedResourceSuggestion
      );

      return oldSection;
    }
    return;
  }

  public trackByFn(index: number) {
    return index;
  }

  public dismissOrgAnnouncements() {
    this.notificationService
      .markNotificationRead(this.orgAnnouncementNotification.notificationId)
      .subscribe(() => {
        this.orgAnnouncementNotification = null;
        this.cdr.markForCheck();
      });
  }

  // This function is passed as an input to a child component
  public loadMore: (index: number) => Promise<unknown> = (index) => {
    const section = this.sections[index];
    section.isLoadingMore = true;

    return section.section$
      .pipe(
        first(),
        switchMap((oldSection) =>
          this.suggestedResourcesService
            .getSuggestedResources({
              section: oldSection.resourceSection,
              useResourceImages: true,
              take: 18 - oldSection.resourceSuggestions.length,
              skip: oldSection.resourceSuggestions.length, // skip already loaded records
            })
            .pipe(
              tap((newSection) => {
                if (!newSection[0]) {
                  return;
                }
                for (const suggestion of newSection[0].resourceSuggestions) {
                  if (
                    !oldSection.resourceSuggestions.find(
                      (oldSuggestion) =>
                        oldSuggestion.userSuggestionId ===
                        suggestion.userSuggestionId
                    )
                  ) {
                    oldSection.resourceSuggestions.push(suggestion);
                  }
                }
              })
            )
        )
      )
      .toPromise()
      .catch((error) => {
        console.error(error);
        this.notifierService.showError(
          this.translateService.instant('InputsSvc_GetSuggestedError')
        );
        throw error;
      })
      .finally(() => {
        section.isLoadingMore = false;
      });
  };

  /**
   * Toggle the isExpanded state for the given section
   */
  public toggleExpanded(index: number, element: HTMLElement) {
    this.sections[index].isExpanded = !this.sections[index].isExpanded;

    // This handler can only be triggered when the section has loaded
    this.learnerHomeTrackerService.sectionExpandedCollapsed(
      element,
      this.sections[index].isExpanded
    );
  }

  public sectionImpressed(index: number, element: HTMLElement) {
    if (!this.sections[index + 1]) {
      this.sectionsToLoad$.next(index + 1);
    }

    this.sections[index].section$.pipe(first()).subscribe((_section) => {
      // Wait for the newly received data to render so we have something to report
      setTimeout(() => {
        this.learnerHomeTrackerService.sectionImpressed(element);
      });
    });
  }

  private addLoadingSection(sections: Section[]): Section {
    const section: Section = {
      isLoading: true,
      isLoadingMore: false,
      isExpanded: false,
      section$: new ReplaySubject(1),
    };
    sections.push(section);
    return section;
  }

  private createSectionLoader(sections: Section[]) {
    const sectionstoLoad$ = new Subject<number>();

    sectionstoLoad$
      .pipe(
        // Add skeletons for all of the loading sections
        map((idx) => ({ idx, section: this.addLoadingSection(sections) })),
        // Since we're changing the sections array, make sure that's reflected on the page
        tap(() => this.cdr.detectChanges()),
        // Use concatMap to ensure there's only one active request at the time because the server must calculate
        // the previous sections before it can get started on the later ones.
        concatMap(({ idx, section }) => {
          return zip(
            of(section),
            // It's possible we get here, but 'section' has already been removed if we're at the end,
            // so there is no need to make a request if we know it's not going to return anything.
            sections.indexOf(section) !== -1
              ? this.suggestedResourcesService.getSuggestedResources({
                  sections: 1,
                  sectionsToSkip: idx,
                  useResourceImages: true,
                })
              : of([])
          );
        }),
        // Don't do anything after the component is destroyed
        this.takeUntilDestroyed()
      )
      .subscribe({
        next: ([section, loadedSections]) => {
          if (loadedSections[0]) {
            section.isLoading = false;
            section.section$.next(loadedSections[0]);
          } else {
            // We're at the end, remove the skeleton from the page AND all skeletons after this
            const idx = sections.indexOf(section);
            if (idx !== -1) {
              sections.splice(sections.indexOf(section));
            }
          }
          this.cdr.detectChanges();
        },
      });

    return sectionstoLoad$;
  }
}
